// SlidePreview.jsx
// This component is used to display a preview of a slide in the file viewer.

// Copyright HS Analysis GmbH, 2025
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// External imports
import { LazyLoadImage } from "react-lazy-load-image-component";

// MUI imports
import { makeStyles } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";

// HSA Imports
import Backend from "../../common/utils/Backend";
// import ValidatedImage from "../../viewer/components/ValidatedImage";

const useStyles = makeStyles((theme) => ({
  root: {
    pointerEvents: "none",
    display: "grid",
    gridTemplateColumns: "auto 1fr",
  },
  tagBox: {
    display: "inline-block",
    width: 20,
    height: 20,
    borderRadius: 5,
    color: "#fff",
    border: `1px solid ${theme.palette.primary.inactive}`,
    margin: 2,
  },
  row: {
    cursor: "pointer",
  },
  slide: {
    position: "relative",
    margin: "10px",
    width: "280px",
    height: "100px",
    border: "1px solid #c2c8cb",
    boxShadow: `2px 2px 4px ${theme.palette.primary.inactive}`,
    background: "#c2c8cb",
  },
  inactiveSlide: {
    border: `3px solid ${theme.palette.primary.inactive}`,
    background: "#c2c8cb",
    margin: "3px 0",
    "&:hover >div": {
      backgroundColor: theme.palette.selected.main,
    },
    // Add touch interaction styles
    "@media (hover: none)": {
      "&:active >div": {
        backgroundColor: theme.palette.selected.main,
      },
    },
  },
  activeSlide: {
    border: `3px solid ${theme.palette.primary.main}`,
    background: "#c2c8cb",
    margin: "3px 0",
  },
  splitScreenSlide: {
    border: `3px dashed ${theme.palette.primary.main}`,
    background: "#c2c8cb",
    margin: "3px 0",
  },
  textfield: {
    position: "relative",
    marginTop: 12,
    marginLeft: 5,
    height: "100px",
    width: "150px",
    background: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    boxShadow: `inset 1px 0px 6px ${theme.palette.primary.inactive}`,
    padding: "5px",
    textAlign: "center",
    overflow: "hidden",
    fontSize: "x-small",
  },
  slideFileName: {
    width: "100%",
    wordWrap: "break-word", // Ensures long text wraps
    overflowWrap: "break-word", // Alternative for better compatibility
    textAlign: "center", // Optional: centers the text
  },
  slideAdditionalName: {
    width: "100%",
    position: "absolute",
    top: 0,
    textAlign: "center",
  },
  slideComment: {
    width: "100%",
    position: "absolute",
    bottom: 0,
    textAlign: "center",
  },
  infoLabel: {
    display: "flex",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "15px",
    color: "#808080",
  },
  placeholder: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  "slide-corner": {
    "&::before": {
      position: "absolute",
      content: '""',
      display: "block",
      width: "15px",
      height: "30px",
      background: "#c2c8cb",
      border: "0 solid #c2c8cb",
      boxShadow: `2px 2px 4px ${theme.palette.primary.inactive}`,
    },
    "&::after": {
      position: "absolute",
      content: '""',
      display: "block",
      width: "15px",
      height: "30px",
      background: "#c2c8cb",
      border: "0 solid #c2c8cb",
      boxShadow: `2px 2px 4px ${theme.palette.primary.inactive}`,
    },
  },
  "top-corners": {
    "&::before": {
      borderRadius: "0 0 30px 0",
      borderRightWidth: "2px",
      borderBottomWidth: "10px",
    },
    "&::after": {
      right: "0",
      borderRadius: "0 0 0 30px",
      borderLeftWidth: "2px",
      borderBottomWidth: "10px",
    },
  },
  "bottom-corners": {
    "&::before": {
      bottom: "0",
      borderRadius: "0 30px 0 0",
      borderTopWidth: "10px",
      borderRightWidth: "2px",
    },
    "&::after": {
      right: "0",
      bottom: "0",
      borderRadius: "30px 0 0 0",
      borderTopWidth: "10px",
      borderLeftWidth: "2px",
    },
  },
  "slide-content": {
    position: "absolute",
    left: "5px",
    top: "5px",
    width: "270px",
    height: "90px",
    background: "white",
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    justifyContent: "center",
    "& > div": {
      height: 90,
      marginLeft: 10,
    },
  },
  "slide-thumbnail": {
    display: "block",
    margin: "auto",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    borderRadius: "5px",
  },
  labelContainer: {
    width: 90,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  labelOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 90,
    height: 90,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px dashed #c2c8cb",
    borderRadius: 5,
    color: "#808080",
    fontSize: 10,
    background: "rgba(255,255,255,0.6)",
    pointerEvents: "none",
  },
  thumbnailContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 90,
    position: "relative", // allow absolute overlays (e.g., label spinner)
  },
  labelLoadingOverlay: {
    position: "absolute",
    left: 0,
    top: 0,
    width: 90,
    height: 90,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    background: "transparent",
  },
}));

const SlidePreview = ({
  fileStatus,
  file,
  containerWidth,
  showAdditionalName,
  showComments,
  showLabel = true,
  useAdditionalNameInsteadOfFilename = false,
  scrollContainer,
}) => {
  const classes = useStyles();
  const wrapperRef = React.useRef(null);
  const labelErrorTimerRef = React.useRef(null);
  const [shouldLoad, setShouldLoad] = React.useState(false);
  const [labelError, setLabelError] = React.useState(false);
  const [labelAttempt, setLabelAttempt] = React.useState(0);
  const [labelLoaded, setLabelLoaded] = React.useState(false);
  const [mainLoaded, setMainLoaded] = React.useState(false);
  const [mainError, setMainError] = React.useState(false);
  const MAX_LABEL_RETRIES = 6;
  // Frontend-only rotate handling
  const [needsRotate, setNeedsRotate] = React.useState(false);
  const thumbnailRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  // Determine if file extension is .ndpi (case-insensitive)
  const isNdpi = React.useMemo(() => {
    const name = file?.name || "";
    const ext = name.split(".").pop()?.toLowerCase() || "";
    return ext === "ndpi";
  }, [file?.name]);

  // Compute label URL with a small cache-busting suffix for retries
  const baseLabelUrl = React.useMemo(
    () => Backend.renderSlideLabel(file.id),
    [file?.id]
  );
  const labelSrc = React.useMemo(
    () => `${baseLabelUrl}&_=${labelAttempt}`,
    [baseLabelUrl, labelAttempt]
  );

  // Reset label error when file changes
  React.useEffect(() => {
    if (labelErrorTimerRef.current) {
      clearTimeout(labelErrorTimerRef.current);
      labelErrorTimerRef.current = null;
    }
    setLabelError(false);
    setLabelAttempt(0);
    setLabelLoaded(false);
    setMainLoaded(false);
    setMainError(false);
    setNeedsRotate(false);
  }, [file?.id]);

  React.useEffect(() => {
    if (shouldLoad) return;
    const rootEl =
      typeof scrollContainer === "string"
        ? document.querySelector(scrollContainer)
        : scrollContainer || null;
    const root = rootEl instanceof Element ? rootEl : null;
    const el = wrapperRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setShouldLoad(true);
        });
      },
      { root, threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [scrollContainer, shouldLoad]);

  // Listen for backend rotation of label/macro images and refresh label when matching
  React.useEffect(() => {
    const onRotated = (ev) => {
      const d = ev?.detail || {};
      if (!d) return;
      if (String(d.id) === String(file?.id) && d.attachmentType === "label") {
        // Reset and bump attempt to force reload with cache-busting suffix
        setLabelError(false);
        setLabelLoaded(false);
        setLabelAttempt((n) => n + 1);
      }
    };
    window.addEventListener("hsa:slide-attachment-rotated", onRotated);
    return () =>
      window.removeEventListener("hsa:slide-attachment-rotated", onRotated);
  }, [file?.id]);

  let fileStatusClass = classes.inactiveSlide;
  if (fileStatus === "imgActive") {
    fileStatusClass = classes.activeSlide;
  } else if (fileStatus === "imgSplitScreen") {
    fileStatusClass = classes.splitScreenSlide;
  }

  // Effective label visibility: for .ndpi with showLabel true, do NOT render label (macro-only mode)
  const showLabelEffective = showLabel && !isNdpi;

  // Reserve label column while loading so main spinner centers in remaining space
  const labelLoading =
    showLabelEffective && shouldLoad && !labelLoaded && !labelError;
  const reserveLabelSpace = showLabelEffective && (labelLoaded || labelLoading);
  const showLabelColumn = reserveLabelSpace;
  const gridTemplateColumns = showLabelEffective
    ? reserveLabelSpace
      ? "auto 1fr"
      : "0px 1fr"
    : "1fr";

  // For .ndpi when showLabel is true, render only the macro image; otherwise thumbnail/slide image
  const mainSrc = React.useMemo(() => {
    if (isNdpi && showLabel) {
      // Always fetch original orientation; rotate in frontend only for preview
      return Backend.renderMacroImage(file.id);
    }
    return containerWidth < 900
      ? Backend.renderThumbnail(encodeURIComponent(file.id))
      : Backend.renderSlideImg(encodeURIComponent(file.id));
  }, [containerWidth, file?.id, isNdpi, showLabel]);
  React.useEffect(() => {
    // Reset main image loading state when source changes
    setMainLoaded(false);
    setMainError(false);
  }, [mainSrc]);
  // Ensure canvas-based rotation is cleared when not in NDPI+showLabel macro-only mode
  React.useEffect(() => {
    const macroOnly = isNdpi && showLabel;
    if (!macroOnly) {
      setNeedsRotate(false);
      const c = canvasRef.current;
      if (c) {
        const ctx = c.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, c.width || 0, c.height || 0);
        }
      }
    }
  }, [isNdpi, showLabel, mainSrc]);
  const mainLoading = shouldLoad && !mainLoaded && !mainError;

  return (
    <div className={fileStatusClass} ref={wrapperRef}>
      <div className={classes.root}>
        <div className={classes.textfield}>
          {/* Top line: additionalName */}
          {showAdditionalName &&
            file.additionalName &&
            !useAdditionalNameInsteadOfFilename && (
              <div className={classes.slideAdditionalName}>
                {file.additionalName}
              </div>
            )}
          <span className={classes.slideFileName}>
            {useAdditionalNameInsteadOfFilename
              ? file.additionalName || file.name
              : file.name}
            {file.sceneCount > 1 && ", S" + (file.scene + 1)}
          </span>
          {/* Bottom line: comment */}
          {showComments && file.comment && (
            <div className={classes.slideComment}>{file.comment}</div>
          )}
        </div>
        <div style={{ marginRight: 26 }}>
          <div className={classes.slide}>
            <div
              className={classes["slide-content"]}
              style={{ gridTemplateColumns }}
            >
              {showLabelEffective && (
                <div
                  className={classes.labelContainer}
                  style={{
                    width: reserveLabelSpace ? 90 : 0,
                    overflow: "hidden",
                  }}
                >
                  {!shouldLoad ? (
                    <div
                      className={classes.placeholder}
                      style={{ width: 90, height: 90 }}
                    >
                      <CircularProgress size={20} />
                    </div>
                  ) : (
                    <>
                      {!labelError && (
                        <LazyLoadImage
                          key={labelAttempt}
                          draggable="false"
                          height={90}
                          src={labelSrc}
                          alt="Slide Label"
                          placeholder={
                            <div
                              className={classes.placeholder}
                              style={{ width: 90, height: 90 }}
                            >
                              <CircularProgress size={20} />
                            </div>
                          }
                          threshold={0.15}
                          visibleByDefault={shouldLoad}
                          onError={() => {
                            if (labelAttempt < MAX_LABEL_RETRIES) {
                              setLabelAttempt((n) => n + 1);
                            } else {
                              // Give up quietly without showing a placeholder
                              if (labelErrorTimerRef.current) {
                                clearTimeout(labelErrorTimerRef.current);
                              }
                              labelErrorTimerRef.current = setTimeout(() => {
                                setLabelError(true);
                                labelErrorTimerRef.current = null;
                              }, 150);
                            }
                          }}
                          onLoad={() => {
                            if (labelErrorTimerRef.current) {
                              clearTimeout(labelErrorTimerRef.current);
                              labelErrorTimerRef.current = null;
                            }
                            setLabelError(false);
                            setLabelLoaded(true);
                          }}
                        />
                      )}
                      {labelLoading && (
                        <div className={classes.labelLoadingOverlay}>
                          <CircularProgress size={18} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              <div
                ref={thumbnailRef}
                className={classes.thumbnailContainer}
                style={{ marginLeft: showLabelColumn ? undefined : 0 }}
              >
                {/* Removed overlay when reserving label space; spinner shows in label column */}
                {/* Canvas used only when a rotation is needed (frontend-only) */}
                <canvas
                  ref={canvasRef}
                  style={{
                    display: needsRotate ? "block" : "none",
                    width: "100%",
                    height: "100%",
                    borderRadius: 5,
                  }}
                />
                {shouldLoad ? (
                  <LazyLoadImage
                    className={classes["slide-thumbnail"]}
                    key={mainSrc}
                    src={mainSrc}
                    alt={
                      isNdpi && showLabel ? "Slide Macro" : "Slide Thumbnail"
                    }
                    style={needsRotate ? { display: "none" } : undefined}
                    placeholder={
                      <div className={classes.placeholder}>
                        <CircularProgress size={20} />
                      </div>
                    }
                    threshold={0.15}
                    visibleByDefault={shouldLoad}
                    onLoad={(e) => {
                      // For NDPI macro-only mode, rotate left (counter-clockwise) when width < height using canvas
                      if (isNdpi && showLabel) {
                        try {
                          const img = e?.target;
                          const w = img?.naturalWidth || 0;
                          const h = img?.naturalHeight || 0;
                          if (w > 0 && h > 0 && w < h) {
                            const container = thumbnailRef.current;
                            const canvas = canvasRef.current;
                            if (container && canvas) {
                              const Wc = container.clientWidth || 270;
                              const Hc = container.clientHeight || 90;
                              const dpr = window.devicePixelRatio || 1;
                              canvas.width = Math.max(1, Math.floor(Wc * dpr));
                              canvas.height = Math.max(1, Math.floor(Hc * dpr));
                              canvas.style.width = Wc + "px";
                              canvas.style.height = Hc + "px";

                              const ctx = canvas.getContext("2d");
                              if (ctx) {
                                ctx.setTransform(1, 0, 0, 1, 0, 0);
                                ctx.clearRect(
                                  0,
                                  0,
                                  canvas.width,
                                  canvas.height
                                );
                                ctx.save();
                                ctx.scale(dpr, dpr);
                                ctx.translate(Wc / 2, Hc / 2);
                                ctx.rotate(-Math.PI / 2); // counter-clockwise 90°
                                const scale = Math.min(Wc / h, Hc / w);
                                ctx.scale(scale, scale);
                                ctx.drawImage(img, -w / 2, -h / 2);
                                ctx.restore();
                                setNeedsRotate(true);
                                setMainLoaded(true);
                                setMainError(false);
                                return;
                              }
                            }
                          }
                        } catch (_) {
                          // ignore; fall through to default behavior
                        }
                      }
                      // Default behavior for non-rotated previews
                      setMainLoaded(true);
                      setMainError(false);
                    }}
                    onError={() => {
                      setMainError(true);
                    }}
                  />
                ) : (
                  <div className={classes.placeholder}>
                    <CircularProgress size={20} />
                  </div>
                )}
                {mainLoading && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <CircularProgress size={20} />
                  </div>
                )}
              </div>
            </div>
            <div
              className={`${classes["slide-corner"]} ${classes["top-corners"]}`}
            ></div>
            <div
              className={`${classes["slide-corner"]} ${classes["bottom-corners"]}`}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

SlidePreview.propTypes = {
  fileStatus: PropTypes.string.isRequired,
  file: PropTypes.object.isRequired,
  containerWidth: PropTypes.number.isRequired,
  showAdditionalName: PropTypes.bool,
  showComments: PropTypes.bool,
  showLabel: PropTypes.bool,
  useAdditionalNameInsteadOfFilename: PropTypes.bool,
  scrollContainer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default SlidePreview;
